import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getBucketsCustomData } from '../../buckets/state/buckets.actions';
import Debouncer from '../../helpers/Debouncer';

/**
 * Fetches bucket custom data on demand for the given bucket ids (eg. once a bucket is
 * actually selected in a dropdown/table/attribute/gps field), instead of eagerly
 * fetching it for every bucket under a template. Skips ids that already have custom
 * data loaded so we don't fire duplicate requests.
 *
 * @param {string[]} bucketIds - ids of the buckets currently selected/relevant to a field
 */
export default function useOnDemandBucketCustomData(bucketIds = []) {
  const dispatch = useDispatch();
  const buckets = useSelector((state) => state.buckets.buckets);
  const bucketTemplateMap = useSelector((state) => state.buckets.bucketTemplateMap);

  const debouncerRef = useRef();
  if (!debouncerRef.current) debouncerRef.current = new Debouncer();

  const bucketMap = useMemo(() => {
    const map = new Map();
    Object.values(bucketTemplateMap).forEach((templateBuckets) => {
      templateBuckets.forEach((bucket) => map.set(bucket.id, bucket));
    });
    buckets.forEach((bucket) => map.set(bucket.id, bucket));
    return map;
  }, [buckets, bucketTemplateMap]);

  const idsKey = useMemo(() => (
    Array.from(new Set((bucketIds ?? []).filter(Boolean))).sort().join(',')
  ), [bucketIds]);

  useEffect(() => {
    if (!idsKey) return;
    const relevantIds = idsKey.split(',');
    const idsNeedingCustomData = relevantIds.filter(
      (bucketId) => bucketMap.get(bucketId) && !bucketMap.get(bucketId).customData,
    );

    if (idsNeedingCustomData.length) {
      debouncerRef.current.debounce(
        () => dispatch(getBucketsCustomData(idsNeedingCustomData)),
        250,
      );
    }
  }, [idsKey, bucketMap, dispatch]);

  useEffect(() => () => debouncerRef.current?.clear(), []);
}
